import { useFormikContext } from "formik";
import { FC, useEffect, useMemo } from "react";

import { resolveImportedSourceIds } from "../popupImportSourceOptions";
import { usePopupImportSourceOptions } from "../PopupImportSourceOptionsContext";
import type { PopupImportFormValues } from "../validationSchema";

/**
 * When source options or per-row source id arrays change: map CSV values (DB id, slug,
 * Relay id, etc.) to canonical GraphQL ids; drop unknowns; keep empty FieldArray slots.
 */
export const PopupImportSourceIdsSync: FC = () => {
  const { sourceOptions } = usePopupImportSourceOptions();
  const { values, setValues } = useFormikContext<PopupImportFormValues>();

  const optionsKey = useMemo(
    () =>
      sourceOptions
        .map((o) => `${o.value}\t${o.slug}`)
        .sort()
        .join("|"),
    [sourceOptions]
  );

  const rowSourceArraysKey = useMemo(
    () =>
      JSON.stringify(
        values.popups.map((r) => [r.group.sourceIds, r.group.excludeSourceIds])
      ),
    [values.popups]
  );

  useEffect(() => {
    void setValues((prev) => {
      let changed = false;
      const nextPopups = prev.popups.map((row) => {
        const origS = row.group.sourceIds ?? [];
        const origE = row.group.excludeSourceIds ?? [];
        const s = resolveImportedSourceIds(origS, sourceOptions);
        const e = resolveImportedSourceIds(origE, sourceOptions);
        if (
          JSON.stringify(s) === JSON.stringify(origS) &&
          JSON.stringify(e) === JSON.stringify(origE)
        ) {
          return row;
        }
        changed = true;
        return {
          ...row,
          group: { ...row.group, sourceIds: s, excludeSourceIds: e },
        };
      });
      return changed ? { popups: nextPopups } : prev;
    });
  }, [optionsKey, rowSourceArraysKey, setValues, sourceOptions]);

  return null;
};
